import React, { useEffect, useState, useCallback } from 'react';
import { systemApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { 
  HardDrive, 
  Database, 
  FileText, 
  Users, 
  FolderOpen,
  RefreshCw,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Building2
} from 'lucide-react';
import { toast } from 'sonner';

const StorageStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStats = useCallback(async (showToast = false) => {
    try {
      if (showToast) setRefreshing(true);
      const data = await systemApi.getStorageStats();
      setStats(data);
      if (showToast) toast.success('Storage stats refreshed');
    } catch (error) {
      console.error('Failed to fetch storage stats:', error);
      if (showToast) toast.error('Failed to refresh stats');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    // Refresh every 5 minutes
    const interval = setInterval(() => fetchStats(), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const getWarningIcon = (level) => {
    switch (level) {
      case 'critical':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'caution':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      default:
        return <CheckCircle className="w-5 h-5 text-green-500" />;
    }
  };

  const getWarningColor = (level) => {
    switch (level) {
      case 'critical':
        return 'bg-red-500';
      case 'warning':
        return 'bg-amber-500';
      case 'caution':
        return 'bg-yellow-500';
      default:
        return 'bg-green-500';
    }
  };

  const getWarningBgColor = (level) => {
    switch (level) {
      case 'critical':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-amber-50 border-amber-200';
      case 'caution':
        return 'bg-yellow-50 border-yellow-200';
      default:
        return 'bg-green-50 border-green-200';
    }
  };

  const getWarningMessage = (level, percent) => {
    switch (level) {
      case 'critical':
        return `CRITICAL: Storage almost full (${percent}% used). Delete old files or expand storage immediately!`;
      case 'warning':
        return `WARNING: Storage getting full (${percent}% used). Consider cleaning up old attachments.`;
      case 'caution':
        return `CAUTION: Storage usage above 70% (${percent}% used). Monitor closely.`;
      default:
        return `Storage healthy (${percent}% used)`;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="w-5 h-5" />
            System Storage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  const { disk, uploads, database } = stats;

  return (
    <Card className="col-span-full" data-testid="storage-stats-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <HardDrive className="w-5 h-5 text-[#6a5da4]" />
            System Storage & Stats
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fetchStats(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Warning Banner */}
        {disk.warningLevel !== 'normal' && (
          <div className={`p-3 rounded-lg border flex items-start gap-3 ${getWarningBgColor(disk.warningLevel)}`}>
            {getWarningIcon(disk.warningLevel)}
            <div>
              <p className="font-medium text-sm">
                {disk.warningLevel === 'critical' ? 'Critical Storage Alert' : 
                 disk.warningLevel === 'warning' ? 'Storage Warning' : 'Storage Notice'}
              </p>
              <p className="text-sm text-muted-foreground">
                {getWarningMessage(disk.warningLevel, disk.percentUsed)}
              </p>
            </div>
          </div>
        )}

        {/* Disk Usage */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2">
              {getWarningIcon(disk.warningLevel)}
              <span className="font-medium">Disk Storage</span>
            </span>
            <span className="text-muted-foreground">
              {disk.usedFormatted} / {disk.totalFormatted}
            </span>
          </div>
          <div className="relative">
            <Progress 
              value={disk.percentUsed} 
              className="h-4"
            />
            <div 
              className={`absolute top-0 left-0 h-4 rounded-full transition-all ${getWarningColor(disk.warningLevel)}`}
              style={{ width: `${disk.percentUsed}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{disk.percentUsed}% used</span>
            <span className="text-green-600 font-medium">{disk.freeFormatted} free</span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
          {/* Uploads */}
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
            <div className="flex items-center gap-2 mb-1">
              <FolderOpen className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-800">Attachments</span>
            </div>
            <p className="text-lg font-bold text-blue-900">{uploads.fileCount.toLocaleString()}</p>
            <p className="text-xs text-blue-600">{uploads.sizeFormatted}</p>
          </div>

          {/* Vouchers */}
          <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-4 h-4 text-purple-600" />
              <span className="text-xs font-medium text-purple-800">Vouchers</span>
            </div>
            <p className="text-lg font-bold text-purple-900">{database.vouchers.toLocaleString()}</p>
            <p className="text-xs text-purple-600">Total records</p>
          </div>

          {/* Users */}
          <div className="bg-green-50 rounded-lg p-3 border border-green-100">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-green-600" />
              <span className="text-xs font-medium text-green-800">Users</span>
            </div>
            <p className="text-lg font-bold text-green-900">{database.users.toLocaleString()}</p>
            <p className="text-xs text-green-600">Registered</p>
          </div>

          {/* Departments */}
          <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="w-4 h-4 text-amber-600" />
              <span className="text-xs font-medium text-amber-800">Departments</span>
            </div>
            <p className="text-lg font-bold text-amber-900">{database.departments.toLocaleString()}</p>
            <p className="text-xs text-amber-600">Active</p>
          </div>
        </div>

        {/* Database Stats */}
        <div className="pt-2 border-t">
          <div className="flex items-center gap-2 mb-2">
            <Database className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Database Records</span>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <span>Audit Logs: <strong>{database.auditLogs.toLocaleString()}</strong></span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StorageStats;
